/* eslint-disable no-empty-function */
import {
  type ComponentProps,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import TableFunctions from "@/components/reusables/data-table/Functions";
import Filters from "@/components/reusables/data-table/filter/Filters";

type TResetFilter = {
  handleClearFilter: () => void;
};

type FilterType<T = unknown, P = unknown> = ComponentProps<
  typeof Filters<T, P>
> &
  TResetFilter;
type TResetSearch = {
  resetSearch: () => void;
};

type FunctionType = ComponentProps<typeof TableFunctions> & TResetSearch;

type TDataTableSlotContext<T = unknown, P = unknown> = {
  filters: ReactNode | null;
  functions: ReactNode | null;
  setFilters: (filters: FilterType<T, P>) => void;
  setFunctions: (functions: FunctionType) => void;
  resetPagination: (() => void) | undefined;
  resetFilter: (() => void) | undefined;
  resetSorting: (() => void) | undefined;
  resetSearch: (() => void) | undefined;
};

export const DataTableSlotContext = createContext<TDataTableSlotContext>({
  filters: null,
  functions: null,
  setFilters: () => {},
  setFunctions: () => {},
  resetPagination: () => {},
  resetFilter: () => {},
  resetSorting: () => {},
  resetSearch: () => {},
});

export const useDataTableSlotContext = <T, P>(): TDataTableSlotContext<
  T,
  P
> => {
  return useContext(DataTableSlotContext) as TDataTableSlotContext<T, P>;
};

export const DataTableSlotContextProvider = <T, P>({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [filters, setFilters] = useState<FilterType<T, P> | null>(null);
  const [functions, setFunctions] = useState<FunctionType | null>(null);

  // const [resetFilter, setResetFilter] = useState<TresetFilter | null>(null);

  const resetFilter = useMemo(() => {
    return filters?.handleClearFilter;
  }, [functions]);

  const resetPagination = useMemo(() => {
    return functions?.functions?.pagination?.resetPagination;
  }, [functions]);
  const resetSorting = useMemo(() => {
    return functions?.functions?.pagination?.resetSorting;
  }, [functions]);

  const resetSearch = useMemo(() => {
    return functions?.resetSearch;
  }, [functions]);

  return (
    <DataTableSlotContext.Provider
      value={{
        filters: filters ? <Filters {...filters} /> : null,
        functions: functions ? <TableFunctions {...functions} /> : null,
        setFilters: setFilters as (
          filters: FilterType<unknown, unknown>,
        ) => void,
        setFunctions,
        resetPagination,
        resetSorting,
        resetFilter,
        resetSearch,
      }}
    >
      {children}
    </DataTableSlotContext.Provider>
  );
};

export default DataTableSlotContextProvider;
